import { PropertyType } from '~/client/graph/types.generated'
import {
  RESOURCE_STATIC_FIELDS,
  ResourceTypeId,
} from '~/client/src/shared/constants/resourceTypes'
import type { OptionGlyph } from '~/client/src/shared/types/OptionGlyph'
import { getHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'

import {
  buildResourceListProperties,
  getResourceFieldLabel,
} from '../builders/buildResourceListProperties'
import { isDynamicResourcePropertyFilterable } from '../builders/resourcePropertyFilterability'
import {
  getResourceDefinition,
  isResourcePropertyBackedField,
} from '../definitions'
import { IResourceFieldDefinition } from '../definitions/types'
import { IResourceLinkConfig } from '../resourceLink'
import { IResourceLinkConfiguration } from '../resourceLink/targets'
import { IResourceFieldCatalog, IResourceFieldDescriptor } from './types'

const STATIC_FIELD_SORTABLE_KEYS = new Set(['name', 'stxResourceId'])
const STATIC_FIELD_GROUPABLE_KEYS = new Set(['name', 'stxResourceId'])

const getStaticFieldPropertyType = (
  type: 'text' | 'number' | 'select',
): PropertyType => {
  switch (type) {
    case 'number':
      return PropertyType.Number
    case 'select':
      return PropertyType.Select
    default:
      return PropertyType.Text
  }
}

const toPropertyConfiguration = (property?: IResourceFieldCatalogProperty) =>
  property?.configuration
    ? {
        multiline: property.configuration.multiline,
        options: property.configuration.options,
        unit: property.configuration.unit,
        format: property.configuration.format,
        resourceLink: property.configuration.resourceLink,
        includeNoneOption: property.configuration.includeNoneOption,
      }
    : undefined

const mapFieldDataTypeToPropertyType = (
  fieldDefinition: IResourceFieldDefinition,
): PropertyType => {
  switch (fieldDefinition.dataType) {
    case 'number':
      return PropertyType.Number
    case 'datetime':
      return PropertyType.Datetime
    case 'checkbox':
      return PropertyType.Checkbox
    case 'select':
      return PropertyType.Select
    case 'location':
      return PropertyType.ResourceLink
    default:
      return PropertyType.Text
  }
}

const shouldMaterializeCatalogField = (
  fieldDefinition: IResourceFieldDefinition,
): boolean =>
  !!(
    fieldDefinition.table?.sortable ||
    fieldDefinition.table?.groupable ||
    fieldDefinition.table?.filterable ||
    fieldDefinition.table?.searchable ||
    fieldDefinition.table?.column
  )

// Property types whose meaning design maps to a canonical Group by / Sort by glyph.
const PROPERTY_TYPE_GLYPHS: Partial<Record<PropertyType, OptionGlyph>> = {
  [PropertyType.Text]: 'text',
  [PropertyType.Number]: 'number',
  [PropertyType.Select]: 'select',
  [PropertyType.Hierarchy]: 'hierarchy',
  [PropertyType.Checkbox]: 'checkbox',
  [PropertyType.Datetime]: 'datetime',
  [PropertyType.User]: 'user',
  [PropertyType.Company]: 'company',
  [PropertyType.File]: 'file',
  [PropertyType.Rollup]: 'number',
}

// A relation's glyph names the resource it links to.
const RESOURCE_LINK_TARGET_GLYPHS: Partial<Record<string, OptionGlyph>> = {
  [ResourceTypeId.LOCATIONS]: 'location',
  [ResourceTypeId.SENSORS]: 'sensor',
}

const STATIC_FIELD_GLYPHS: Record<string, OptionGlyph> = {
  name: 'name',
  stxResourceId: 'id',
}

const getPropertyTypeGlyph = (
  propertyType: PropertyType | string,
  configuration?: IResourceLinkConfiguration,
): OptionGlyph | undefined =>
  propertyType === PropertyType.ResourceLink
    ? RESOURCE_LINK_TARGET_GLYPHS[
        configuration?.resourceLink?.targetResourceType ?? ''
      ]
    : PROPERTY_TYPE_GLYPHS[propertyType as PropertyType]

const buildStaticFieldDescriptors = (
  resourceTypeId?: string,
): IResourceFieldDescriptor[] => {
  const fields = resourceTypeId
    ? RESOURCE_STATIC_FIELDS[resourceTypeId] || []
    : []

  return fields.map((field, index) => {
    const propertyType = getStaticFieldPropertyType(field.type)

    return {
      key: field.name,
      label: field.label,
      dataType: field.type,
      propertyType,
      capabilities: {
        sortable: STATIC_FIELD_SORTABLE_KEYS.has(field.name),
        groupable: STATIC_FIELD_GROUPABLE_KEYS.has(field.name),
        filterable: !!field.isFacetable,
        searchable: !!field.isSearchable,
      },
      glyph:
        STATIC_FIELD_GLYPHS[field.name] ?? getPropertyTypeGlyph(propertyType),
      source: 'static',
      position: -1000 + index,
    }
  })
}

export interface IResourceFieldCatalogProperty {
  name: string
  displayName?: string | null
  propertyType: PropertyType | string
  configuration?: {
    multiline?: boolean
    options?: Array<{
      value: string
      label: string
      parentValue?: string
      deleted?: boolean
    }>
    unit?: string
    format?: string
    resourceLink?: IResourceLinkConfig | null
    includeNoneOption?: boolean | null
  }
  position?: number
  isDefinitionOnly?: boolean
}

const buildPropertyDescriptor = (
  resourceTypeId: string | undefined,
  property: IResourceFieldCatalogProperty,
): IResourceFieldDescriptor => {
  const definition = resourceTypeId
    ? getResourceDefinition(resourceTypeId)
    : null
  const fieldDefinition = definition?.fields[property.name]
  const syntheticDefinition = definition?.map?.syntheticFields?.[property.name]
  const resolvedDefinition = fieldDefinition || syntheticDefinition
  const isDynamicProperty = !resolvedDefinition || !property.isDefinitionOnly
  // The block, not the definition it sits in, is what overrides the property's own type:
  // a definition describing only a card leaves it out and keeps the defaults below, while
  // `table: {}` opts every capability out (latitude, longitude).
  const tableDefinition = resolvedDefinition?.table

  const capabilities = tableDefinition
    ? {
        sortable: !!tableDefinition.sortable,
        groupable: !!tableDefinition.groupable,
        filterable: !!tableDefinition.filterable,
        searchable: !!tableDefinition.searchable,
      }
    : {
        sortable: isDynamicProperty,
        groupable: isDynamicProperty,
        filterable: isDynamicResourcePropertyFilterable(property as any),
        searchable: true,
      }

  const source = syntheticDefinition
    ? 'synthetic'
    : fieldDefinition
      ? 'definition'
      : property.isDefinitionOnly
        ? 'definition'
        : 'dynamic-property'

  const configuration = toPropertyConfiguration(property)
  const hierarchy =
    property.propertyType === PropertyType.Hierarchy &&
    configuration?.options?.length
      ? getHierarchyAccessors(configuration.options)
      : undefined

  return {
    key: property.name,
    label: getResourceFieldLabel(
      resourceTypeId || '',
      property.name,
      resolvedDefinition,
      [property],
    ),
    dataType: resolvedDefinition?.dataType || String(property.propertyType),
    propertyType: property.propertyType,
    configuration,
    capabilities,
    hierarchy,
    cellType: tableDefinition?.cellType,
    glyph:
      resolvedDefinition?.glyph ??
      getPropertyTypeGlyph(property.propertyType, configuration),
    source,
    position: property.position ?? 0,
  }
}

export const buildResourceFieldCatalog = (
  resourceTypeId?: string,
  properties?: IResourceFieldCatalogProperty[] | null,
): IResourceFieldCatalog => {
  const definition = getResourceDefinition(resourceTypeId)
  const materializedProperties = buildResourceListProperties(
    resourceTypeId,
    properties as any,
  ).map(property => {
    const sourceProperty = properties?.find(item => item.name === property.name)

    return sourceProperty
      ? {
          ...property,
          displayName: sourceProperty.displayName || property.displayName,
          propertyType: sourceProperty.propertyType,
          configuration: sourceProperty.configuration || property.configuration,
          position: sourceProperty.position ?? property.position,
        }
      : property
  })
  const existingKeys = new Set(
    materializedProperties.map(property => property.name),
  )
  const definitionOnlyCatalogProperties: IResourceFieldCatalogProperty[] = [
    ...Object.entries(definition?.fields || {}).filter(
      ([key]) => !isResourcePropertyBackedField(resourceTypeId, key),
    ),
    ...Object.entries(definition?.map?.syntheticFields || {}),
  ]
    .filter(
      ([key, fieldDefinition]) =>
        !existingKeys.has(key) &&
        shouldMaterializeCatalogField(fieldDefinition),
    )
    .map(([key, fieldDefinition], index) => ({
      name: key,
      displayName: getResourceFieldLabel(
        resourceTypeId || '',
        key,
        fieldDefinition,
      ),
      propertyType: mapFieldDataTypeToPropertyType(fieldDefinition),
      position: materializedProperties.length + index,
      isDefinitionOnly: true,
    }))
  const descriptors = [
    ...buildStaticFieldDescriptors(resourceTypeId),
    ...materializedProperties.map(property =>
      buildPropertyDescriptor(resourceTypeId, property),
    ),
    ...definitionOnlyCatalogProperties.map(property =>
      buildPropertyDescriptor(resourceTypeId, property),
    ),
  ]

  const columnOrderIndex = new Map(
    (definition?.table?.columnOrder || []).map((key, index) => [key, index]),
  )

  const dedupedFields = Array.from(
    new Map(descriptors.map(field => [field.key, field])).values(),
  ).sort((a, b) => {
    const aOrder = columnOrderIndex.get(a.key) ?? Infinity
    const bOrder = columnOrderIndex.get(b.key) ?? Infinity
    if (aOrder !== bOrder) return aOrder - bOrder
    return a.position - b.position
  })

  const byKey = new Map(dedupedFields.map(field => [field.key, field]))
  const getField = (key: string) => byKey.get(key)

  return {
    resourceTypeId,
    fields: dedupedFields,
    byKey,
    getField,
    sortableFields: dedupedFields.filter(field => field.capabilities.sortable),
    groupableFields: dedupedFields.filter(
      field => field.capabilities.groupable,
    ),
    filterableFields: dedupedFields.filter(
      field => field.capabilities.filterable,
    ),
    searchableFields: dedupedFields.filter(
      field => field.capabilities.searchable,
    ),
  }
}

// Fields ordered as the properties are listed.
export const buildResourceFieldCatalogFromProperties = (
  resourceTypeId: string,
  properties?: Omit<IResourceFieldCatalogProperty, 'position'>[] | null,
): IResourceFieldCatalog =>
  buildResourceFieldCatalog(
    resourceTypeId,
    (properties || []).map((property, index) => ({
      ...property,
      position: index,
    })),
  )
